import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  useController,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppConfig } from "../../api/config/config";
import { EndPoints } from "../../Constants/endpoints";
import { useGetData } from "../../hooks";
import { ICountries } from "../../interface/GeneralResponse";
import { setValidationVal } from "../../redux/reducers/phoneValidationSlice";
import Modal from "../Common/Modal";
import i18next from "i18next";
import { isArabic } from "../../utils/utils";

interface AutocompleteInputProps<TData, TOption> {
  name: string;
  control: Control<any>;
  placeholder?: string;
  required?: boolean;
  getEndpoint: string;
  minSearchLength?: number;
  onOptionSelect?: (option: TOption) => void;
  getOptionLabel: (option: TOption) => {
    value: string;
    label: string | ReactNode;
  };
  getOptionValue: (option: TOption) => any;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  listClassName?: string;
  itemClassName?: string;
  controllerName?: string;
  defaultPhone?: string;
  CountryId?: number | null;
  isOptional?: boolean;
  readOnly?: boolean;
  className?: string;
  searchParamKey?: string;
  onSearchResults?: (options: TOption[]) => void;
  onInputChange?: () => void;
}

const PhoneAutocompleteInput = <TData, TOption>({
  name,
  control,
  placeholder,
  required,
  getEndpoint,
  minSearchLength = 9,
  onOptionSelect,
  getOptionLabel,
  getOptionValue,
  inputProps,
  listClassName,
  itemClassName,
  controllerName,
  defaultPhone,
  CountryId = 1,
  isOptional = false,
  className,
  onSearchResults,
  readOnly,
  onInputChange: onInputChange,
}: AutocompleteInputProps<TData, TOption>) => {
  const {
    field: { onChange, onBlur, ref },
  } = useController({
    name,
    control,
    // rules: { required },
  });

  const [inputValue, setInputValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isQueryEnabled = (inputValue?.length ?? 0) >= minSearchLength;

  const endpoint = getEndpoint;

  const { data, isLoading, isSuccess } = useGetData<any[]>(endpoint, {
    // staleTime:0,
    enabled: isQueryEnabled,
    filter: {
      Name: searchValue,
      count: 100,
    },
  });

  const options = data?.Data;

  const handleSearch = (inputValue: string) => {
    if ((inputValue?.length ?? 0) >= minSearchLength) {
      setSearchValue(inputValue);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);

      setSearchValue("");
    }
  };

  const handleOptionSelect = (option: TOption) => {
    const optionLabel = getOptionLabel(option)?.value;
    setInputValue(optionLabel);
    onChange(option);
    setShowSuggestions(false);
    onOptionSelect?.(option);
  };

  const [selectedCountry, setSelectedCountry] = useState<ICountries | null>(
    null,
  );
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.slice(0, 2);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { data: countries } = useGetData<ICountries[]>(EndPoints.Countries);
  const countriesData = countries?.Data;

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (countriesData && countriesData.length > 0) {
      const preselectedCountry = countriesData.find(
        (country: ICountries) => country.Id === CountryId,
      );
      if (preselectedCountry) {
        setSelectedCountry(preselectedCountry);
        dispatch(
          setValidationVal({
            Length: preselectedCountry.PhoneNumberLength,
            Start: preselectedCountry.PhoneNumberStart,
            Code: preselectedCountry.Code,
            CountryId: preselectedCountry.Id,
          }),
        );
      } else {
        setSelectedCountry(countriesData[0]);
      }
    }
  }, [countriesData, CountryId]);

  const handleSelectChange = (option: ICountries) => {
    setSelectedCountry(option);
    setIsModalOpen(false);
    dispatch(
      setValidationVal({
        Length: option.PhoneNumberLength,
        Start: option.PhoneNumberStart,
        Code: option.Code,
        CountryId: option.Id,
      }),
    );
  };

  const {
    formState: { errors },
    setValue,
  } = useFormContext();

  const error = errors[controllerName || "phone"];

  const errorMessage =
    error && error.message
      ? typeof error.message === "string"
        ? error.message
        : String(error.message)
      : undefined;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch(inputValue);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onSearchResults && onSearchResults(data?.Data);
    }
  }, [isSuccess]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="w-full">
        <div
          dir="ltr"
          className={`flex h-11 w-full max-w-[95dvw] items-center gap-4 rounded-lg border bg-white ${
            isFocused ? "border-2 border-primary" : "border-systemBg"
          } p-1.5 ${isArabic(i18next?.language) && ""} ${className ?? ""}`}
        >
          {selectedCountry && (
            <button
              type="button"
              className={`flex h-[34px] items-center gap-1 rounded-[5px] bg-primary-bg px-2 py-2.5`}
              // onClick={() => setIsModalOpen(true)}
            >
              <img
                src={`${AppConfig.SERVER_URL}${selectedCountry.Flag}`}
                alt="flag"
                className="h-3.5 w-5"
              />
              {selectedCountry.Code}
            </button>
          )}

          <Controller
            name={controllerName || "phone"}
            control={control}
            defaultValue={defaultPhone}
            rules={{
              required: isOptional ? false : t("phone_number_required"),
              validate: (value) => {
                if (isOptional && !value) return true;
                const currentCountry = selectedCountry;
                if (!value) return t("phone_number_required");

                const phoneNumber = value;
                if (
                  currentCountry &&
                  phoneNumber.startsWith(
                    String(currentCountry.PhoneNumberStart),
                  )
                ) {
                  if (phoneNumber.length !== currentCountry.PhoneNumberLength) {
                    return t("phone_number_must_be_and_start_with", {
                      length: currentCountry.PhoneNumberLength,
                      start: currentCountry.PhoneNumberStart,
                    });
                  }
                } else {
                  return t("phone_number_must_start_with", {
                    start1: "0",
                    start2: currentCountry?.PhoneNumberStart || "",
                  });
                }
                return true;
              },
            }}
            render={({ field }) => (
              <input
                autoComplete="off"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                name={controllerName}
                readOnly={readOnly}
                value={inputValue}
                onChange={(event) => {
                  onInputChange && onInputChange();

                  let value = event.target.value;
                  const numericValue = value.replace(/\D/g, "");
                  let maxLength = (selectedCountry?.PhoneNumberLength || 0) + 1;
                  if (
                    numericValue.startsWith(
                      String(selectedCountry?.PhoneNumberStart),
                    )
                  ) {
                    maxLength = selectedCountry?.PhoneNumberLength || 15;
                  }
                  const trimmedValue = numericValue.slice(0, maxLength);
                  setInputValue(trimmedValue);
                  setValue(controllerName || "phone", trimmedValue);

                  field.onChange(trimmedValue);
                  handleSearch(trimmedValue);
                }}
                onKeyDown={handleKeyPress}
                {...inputProps}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasteData = e.clipboardData.getData("text");
                  const digitsOnly = pasteData.replace(/\D/g, "");
                  let maxLength = (selectedCountry?.PhoneNumberLength || 0) + 1;
                  if (
                    digitsOnly.startsWith(
                      String(selectedCountry?.PhoneNumberStart),
                    )
                  ) {
                    maxLength = selectedCountry?.PhoneNumberLength || 15;
                  }

                  const trimmedValue = digitsOnly?.slice(0, maxLength);
                  setInputValue(trimmedValue);
                  handleSearch(trimmedValue);
                  field.onChange(trimmedValue);
                }}
                ref={ref}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  onBlur();
                }}
                placeholder={placeholder}
                className={`hide-arrows ${
                  currentLanguage === "ar" ? "placeholder:text-right" : ""
                } flex-1 appearance-none border-none !bg-transparent text-primary-text placeholder-body-text placeholder:text-base focus:outline-none`}
                maxLength={11}
              />
            )}
          />
        </div>

        {errorMessage && (
          <p className="mt-1 text-xs text-error">{errorMessage}</p>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          header={t("countries")}
          customStyle="h-96"
        >
          <div className="countries-list overflow-y-auto">
            {countriesData &&
              countriesData.map((country: ICountries) => (
                <button
                  key={country.Id}
                  type="button"
                  onClick={() => handleSelectChange(country)}
                  className="flex w-full items-center p-2 hover:bg-primary-bg"
                >
                  <img
                    src={`${AppConfig.SERVER_URL}${country.Flag}`}
                    alt={`${country.Name} flag`}
                    className="me-2 w-7"
                  />
                  {country.Name}
                </button>
              ))}
          </div>
        </Modal>
      </div>

      {showSuggestions && (options?.length ?? 0) > 0 && (
        <ul
          className={`absolute z-50 max-h-60 w-full overflow-y-auto rounded-lg bg-white p-2 shadow-md ${listClassName}`}
          style={{ backgroundColor: "white", zIndex: 1000 }}
        >
          {options?.map((option) => (
            <li
              key={getOptionValue(option)}
              className={`cursor-pointer rounded-lg p-2 hover:bg-primary-bg ${itemClassName}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleOptionSelect(option);
              }}
            >
              {getOptionLabel(option)?.label}
            </li>
          ))}
        </ul>
      )}

      {showSuggestions && isLoading && (
        <div
          className="absolute z-50 w-full border border-primary-bg bg-white p-2"
          style={{ backgroundColor: "white", zIndex: 1000 }}
        >
          {t("loading")}
        </div>
      )}
    </div>
  );
};

export default PhoneAutocompleteInput;
